// src/context/NotificationHubContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as signalR from "@microsoft/signalr";
import { notificationHubUrl } from "../config";

const NotificationHubContext = createContext({
  connection: null,
  notificationsVersion: 0,
  triggerNotificationsRefetch: () => {},
});

export const NotificationHubProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  const [notificationsVersion, setNotificationsVersion] = useState(0);

  useEffect(() => {
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(notificationHubUrl, {
        accessTokenFactory: () =>
          localStorage.getItem("token")?.replace(/"/g, ""),
      })
      .withAutomaticReconnect()
      .build();

    hubConnection
      .start()
      .then(() => setConnection(hubConnection))
      .catch((err) =>
        console.error("Notification hub connection failed:", err)
      );

    return () => {
      hubConnection.stop();
    };
  }, []);

  const triggerNotificationsRefetch = () =>
    setNotificationsVersion((version) => version + 1);

  return (
    <NotificationHubContext.Provider
      value={{ connection, notificationsVersion, triggerNotificationsRefetch }}
    >
      {children}
    </NotificationHubContext.Provider>
  );
};

const useNotificationHubContext = () => useContext(NotificationHubContext);

export const useHubEvent = (eventName, handler) => {
  const ctx = useNotificationHubContext();
  const connection = ctx?.connection;
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!connection) return;

    const callback = (...args) => handlerRef.current(...args);
    connection.on(eventName, callback);

    return () => {
      connection.off(eventName, callback);
    };
  }, [connection, eventName]);
};

// Bumped by triggerNotificationsRefetch() to tell any component that fetches
// notifications on mount (e.g. the Topbar bell) to refetch.
export const useNotificationsRefetchSignal = () =>
  useNotificationHubContext()?.notificationsVersion ?? 0;

export const useTriggerNotificationsRefetch = () =>
  useNotificationHubContext()?.triggerNotificationsRefetch ?? (() => {});
